import { useNavigate, Link } from "react-router-dom";
import React from "react";
import { getAuthorInfo } from "../../../api";

interface Author {
    authorId: number;
    authorEmail: string;
    authorName: string;
    profilePhoto: string;
    bookGender1: string;
    bookGender2: string;
    keyWord: string;
    bootTitle: string;
    bookOtherCount: string;
}

export interface SelectedFilters {
    array: string[];
    genre: string[];
    textType: string[];
}

interface AuthorCardListProps {
    authorCards: Author[] | undefined;
    initialFilters?: SelectedFilters;
}

const AuthorCard: React.FC<AuthorCardListProps> = ({ authorCards, initialFilters }) => {
    const navigate = useNavigate();

    const handleAuthorClick = async (author: Author) => {
        if (author.authorId) {
            navigate(`/author/authordetails/${author.authorId}`, {
                state: { authorEmail: author.authorEmail }
            });
        } else if (author.authorEmail) {
            try {
                const data = await getAuthorInfo({ authorEmail: author.authorEmail });
                if (data && data.authorId) {
                    navigate(`/author/authordetails/${data.authorId}`, {
                        state: { authorEmail: author.authorEmail }
                    });
                } else {
                    navigate(`/author/authordetails/${author.authorName.replace(/\s+/g, '')}`, {
                        state: { authorEmail: author.authorEmail }
                    });
                }
            } catch (error) {
                console.error("Failed to fetch authorInfo in AuthorCard:", error);
                navigate(`/author/authordetails/${author.authorName.replace(/\s+/g, '')}`, {
                    state: { authorEmail: author.authorEmail }
                });
            }
        }
    };

    if (!authorCards || authorCards.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-24 gap-3">
                <div className="w-14 h-14 rounded-full bg-[#f0f0f0] flex items-center justify-center text-2xl">
                    ✍️
                </div>
                <p className="text-[#888] text-[15px]">조건에 맞는 작가가 없습니다.</p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-2 md:grid-cols-1 gap-4" id="authorList">
            {authorCards.map((author, index) => {
                const isKeywordActive = initialFilters?.textType.includes(author.keyWord);
                const isGenre1Active = initialFilters?.genre.includes(author.bookGender1);
                const isGenre2Active = initialFilters?.genre.includes(author.bookGender2);

                const authorTarget = author.authorId 
                    ? `/author/authordetails/${author.authorId}` 
                    : `/author/authordetails/${author.authorName.replace(/\s+/g, '')}`;

                return (
                    <Link
                        key={index}
                        to={authorTarget}
                        state={{ authorEmail: author.authorEmail }}
                        className="group flex items-start gap-5 p-6 md:p-4 bg-white border border-[#f0f0f0] rounded-2xl hover:border-[#baecd4] hover:shadow-[0px_4px_16px_rgba(25,207,122,0.1)] transition-all duration-200 block text-inherit no-underline"
                    >
                        {/* 프로필 사진 */}
                        <div className="flex-shrink-0">
                            <div className="w-[88px] h-[88px] md:w-[68px] md:h-[68px] rounded-full overflow-hidden ring-2 ring-white shadow-[0px_4px_12px_rgba(25,207,122,0.15)]">
                                <img
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                    src={author.profilePhoto
                                        ? `data:image/png;base64,${author.profilePhoto}`
                                        : "/img/profile_sam.jpg"}
                                    alt={author.authorName}
                                />
                            </div>
                        </div>

                        {/* 정보 영역 */}
                        <div className="flex flex-col gap-2 flex-1 min-w-0">
                            {/* 이름 + 키워드 뱃지 */}
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[#222] text-[19px] md:text-[16px] font-bold leading-tight">
                                    {author.authorName}
                                </span>
                                {author.keyWord && (
                                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                                        isKeywordActive
                                            ? 'bg-[#19cf7a] text-white'
                                            : 'bg-[#e8f8f0] text-[#19cf7a]'
                                    }`}>
                                        {author.keyWord}
                                    </span>
                                )}
                            </div>

                            {/* 장르 태그 */}
                            {(author.bookGender1 || author.bookGender2) && (
                                <div className="flex gap-1.5 flex-wrap">
                                    {author.bookGender1 && (
                                        <span className={`text-[12px] font-medium px-2 py-0.5 rounded-md ${
                                            isGenre1Active
                                                ? 'bg-[#e1f7eb] text-[#19cf7a] font-semibold'
                                                : 'bg-[#f5f5f5] text-[#888]'
                                        }`}>
                                            {author.bookGender1}
                                        </span>
                                    )}
                                    {author.bookGender2 && (
                                        <span className={`text-[12px] font-medium px-2 py-0.5 rounded-md ${
                                            isGenre2Active
                                                ? 'bg-[#e1f7eb] text-[#19cf7a] font-semibold'
                                                : 'bg-[#f5f5f5] text-[#888]'
                                        }`}>
                                            {author.bookGender2}
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* 대표작 */}
                            {author.bootTitle && (
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className="text-[#bbb] text-[12px]">📖</span>
                                    <span className="text-[#666] text-[13px] md:text-[12px] truncate">
                                        『{author.bootTitle.length > 14
                                            ? author.bootTitle.slice(0, 14) + '…'
                                            : author.bootTitle}』
                                        {Number(author.bookOtherCount) > 0 && (
                                            <span className="text-[#19cf7a] ml-1">
                                                외 {author.bookOtherCount}권
                                            </span>
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* 화살표 */}
                        <div className="flex-shrink-0 self-center text-[#ccc] group-hover:text-[#19cf7a] transition-colors text-[18px] md:hidden">
                            →
                        </div>
                    </Link>
                );
            })}
        </div>
    );
};

export default AuthorCard;
